(() => {
  if (customElements.get("fake-cart-drawer")) return;

  const MINUS_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-minus" viewBox="0 0 10 2" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 1 1 0 1H1A.5.5 0 0 1 .5 1" clip-rule="evenodd"/></svg>`;
  const PLUS_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-plus" viewBox="0 0 10 10" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M1 4.51a.5.5 0 0 0 0 1h3.5l.01 3.5a.5.5 0 0 0 1-.01V5.5l3.5-.01a.5.5 0 0 0-.01-1H5.5L5.49.99a.5.5 0 0 0-1 .01v3.5l-3.5.01z" clip-rule="evenodd"/></svg>`;
  const REMOVE_ICON_SVG = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M8 1.5V2.5H3C2.44772 2.5 2 2.94772 2 3.5V4.5C2 5.05228 2.44772 5.5 3 5.5H21C21.5523 5.5 22 5.05228 22 4.5V3.5C22 2.94772 21.5523 2.5 21 2.5H16V1.5C16 0.947715 15.5523 0.5 15 0.5H9C8.44772 0.5 8 0.947715 8 1.5Z" fill="currentColor"/><path d="M3.9231 7.5H20.0767L19.1344 20.2216C19.0183 21.7882 17.7135 23 16.1426 23H7.85724C6.28636 23 4.98148 21.7882 4.86544 20.2216L3.9231 7.5Z" fill="currentColor"/></svg>`;
  const DISCOUNT_ICON_SVG = `<svg class="icon icon-discount" viewBox="0 0 12 12" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M7 0h3a2 2 0 0 1 2 2v3a1 1 0 0 1-.3.7l-6 6a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4l6-6A1 1 0 0 1 7 0m2 2a1 1 0 1 0 2 0 1 1 0 0 0-2 0" clip-rule="evenodd"/></svg>`;

  let lastItemKeys = [];
  let checkoutInProgress = false;
  let displayDiscountTiers = null;
  let reconcileInProgress = false;

  /** Set when leaving for checkout; used to clear fake cart if Shopify cart is empty on return. */
  const CHECKOUT_PENDING_KEY = "tlts_fake_cart_checkout_pending";

  function escapeSelector(value) {
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
      return CSS.escape(value);
    }

    return String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function formatMoney(cents) {
    if (typeof Shopify !== "undefined" && typeof Shopify.formatMoney === "function") {
      return Shopify.formatMoney(cents);
    }

    return `$${(cents / 100).toFixed(2)}`;
  }

  function getItemsTotalLabel(drawer, itemCount) {
    const template = drawer?.dataset?.itemsTotalLabel || "Total: __COUNT__ Items";
    return template.replace("__COUNT__", String(itemCount));
  }

  function getLineKey(el) {
    return el?.getAttribute("data-line-key") || el?.dataset?.lineKey || "";
  }

  function sumVariantQuantity(variantId) {
    if (!window.FakeCart) return 0;

    const vid = String(variantId);
    return window.FakeCart.getItems().reduce((sum, item) => {
      if (String(item.id) !== vid) return sum;
      return sum + (item.quantity || 0);
    }, 0);
  }

  function commitLineQuantityInput(input) {
    if (!input || !window.FakeCart) return;

    const key = getLineKey(input);
    if (!key) return;

    const item = window.FakeCart.getItems().find((line) => line.key === key);
    if (!item) return;

    // Free gifts are locked to metaobject "how_many" — ignore manual edits.
    if (isFreeGiftLine(item)) {
      input.value = String(item.quantity);
      return;
    }

    const rawValue = String(input.value ?? "").trim();
    const nextQty = rawValue === "" ? NaN : parseInt(rawValue, 10);

    if (!Number.isFinite(nextQty) || nextQty < 1) {
      input.value = String(item.quantity);
      return;
    }

    if (nextQty === item.quantity) return;

    window.FakeCart.setLineQuantity(key, nextQty);
  }

  function getDisplayDiscountTiers() {
    if (displayDiscountTiers !== null) return displayDiscountTiers;

    const configEl = document.getElementById("FakeCartDisplayDiscountsConfig");
    if (!configEl) {
      displayDiscountTiers = [];
      return displayDiscountTiers;
    }

    try {
      displayDiscountTiers = JSON.parse(configEl.textContent) || [];
    } catch {
      displayDiscountTiers = [];
    }

    return displayDiscountTiers;
  }

  function normalizeDiscountType(value) {
    const normalized = String(value || "").toLowerCase();
    if (normalized.includes("amount")) return "amount";
    return "percentage";
  }

  /**
   * FX rate to convert USD-cent display-discount amounts into the storefront's
   * presentment currency (display discounts are a single USD source of truth).
   * Resolution: explicit window.TLTS_MARKET.rate → Shopify.currency.rate → 1.
   * Returns 1 (no conversion) when in the base currency or when no rate exists,
   * so USD markets are untouched and a missing rate fails safe, not silently wrong.
   */
  function getMarketRate() {
    const market = window.TLTS_MARKET || {};
    if (market.currency && market.base && market.currency === market.base) return 1;

    const explicit = Number(market.rate);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;

    const shopRate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
    if (Number.isFinite(shopRate) && shopRate > 0) return shopRate;

    console.warn(
      "[fake-cart] No FX rate for",
      market.currency,
      "— display discount amounts left in base currency (USD)."
    );
    return 1;
  }

  /**
   * Picks exactly one display discount tier — they do not stack.
   * Example: at 40 items with tiers at 10/20/30/40, only the threshold-40 tier applies.
   */
  function resolveActiveDisplayDiscount(itemCount, tiers) {
    if (!Array.isArray(tiers) || tiers.length === 0 || itemCount <= 0) return null;

    let active = null;

    tiers.forEach((tier) => {
      const threshold = Number(tier.threshold) || 0;
      const discount = Number(tier.discount) || 0;

      if (threshold <= 0 || discount <= 0 || itemCount < threshold) return;

      if (!active || threshold > active.threshold) {
        active = {
          name: tier.name || "",
          discount_type: tier.discount_type,
          discount,
          threshold,
        };
      } else if (threshold === active.threshold && discount > active.discount) {
        active.discount = discount;
      }
    });

    return active;
  }

  /** Strikethrough base: MSRP when present, otherwise pre-volume sale price. */
  function displayCompareCentsForLine(line) {
    return line.compareCents > line.originalCents ? line.compareCents : line.originalCents;
  }

  function calculateDisplayPricing(items, activeDiscount) {
    const lines = items.map((item) => {
      const unitPrice = item.price || 0;
      const unitCompare = item.compare_at_price > unitPrice ? item.compare_at_price : unitPrice;
      const quantity = item.quantity || 0;

      return {
        key: item.key,
        item,
        originalCents: unitPrice * quantity,
        compareCents: unitCompare * quantity,
      };
    });

    const subtotalOriginal = lines.reduce((sum, line) => sum + line.originalCents, 0);
    const totalQuantity = lines.reduce((sum, line) => sum + (line.item.quantity || 0), 0);

    if (!activeDiscount || subtotalOriginal <= 0) {
      const pricedLines = lines.map((line) => {
        const displayCompareCents = line.compareCents > line.originalCents ? line.compareCents : 0;
        return {
          ...line,
          finalCents: line.originalCents,
          displayCompareCents,
        };
      });

      const subtotalCompare = pricedLines.reduce((sum, line) => sum + line.displayCompareCents, 0);

      return {
        activeDiscount: null,
        discountName: "",
        lines: pricedLines,
        subtotalFinal: subtotalOriginal,
        subtotalCompare,
      };
    }

    const discountType = normalizeDiscountType(activeDiscount.discount_type);
    const discountValue = Number(activeDiscount.discount) || 0;

    if (discountType === "amount") {
      // Amount is USD cents off per pack/item (metaobject: 10 = $0.10), applied to
      // each unit — not once per order. Convert to the presentment currency so it
      // matches the (already-converted) line prices. Rate is 1 in USD markets.
      const perUnitCents = Math.round(discountValue * getMarketRate());
      const totalDiscountCents = perUnitCents * totalQuantity;
      const subtotalFinal = Math.max(0, subtotalOriginal - totalDiscountCents);

      const pricedLines = lines.map((line) => ({
        ...line,
        finalCents: Math.max(0, line.originalCents - perUnitCents * (line.item.quantity || 0)),
        displayCompareCents: displayCompareCentsForLine(line),
      }));

      const subtotalCompare = pricedLines.reduce((sum, line) => sum + line.displayCompareCents, 0);

      return {
        activeDiscount,
        discountName: activeDiscount.name || "",
        lines: pricedLines,
        subtotalFinal,
        subtotalCompare,
      };
    }

    const subtotalFinal = Math.round((subtotalOriginal * (100 - discountValue)) / 100);

    let allocated = 0;
    const pricedLines = lines.map((line, index) => {
      let finalCents;

      if (index === lines.length - 1) {
        finalCents = subtotalFinal - allocated;
      } else {
        finalCents = Math.round((subtotalFinal * line.originalCents) / subtotalOriginal);
        allocated += finalCents;
      }

      return {
        ...line,
        finalCents,
        displayCompareCents: displayCompareCentsForLine(line),
      };
    });

    const subtotalCompare = pricedLines.reduce((sum, line) => sum + line.displayCompareCents, 0);

    return {
      activeDiscount,
      discountName: activeDiscount.name || "",
      lines: pricedLines,
      subtotalFinal,
      subtotalCompare,
    };
  }

  function isFreeGiftLine(item) {
    if (window.FakeCartConditionalItems && typeof FakeCartConditionalItems.isFreeGiftLine === "function") {
      return FakeCartConditionalItems.isFreeGiftLine(item);
    }
    return Boolean(item && item.properties && String(item.properties._is_free_gift) === "true");
  }

  /** Hidden free gifts stay in storage and checkout, but are omitted from the drawer. */
  function isHiddenInCart(item) {
    if (window.FakeCartConditionalItems && typeof FakeCartConditionalItems.isHiddenInCart === "function") {
      return FakeCartConditionalItems.isHiddenInCart(item);
    }
    return false;
  }

  /** Item count for volume display discounts — free gifts must not inflate tiers. */
  function getPaidItemCount(items) {
    return (items || []).reduce((sum, item) => {
      if (isFreeGiftLine(item)) return sum;
      return sum + (item.quantity || 0);
    }, 0);
  }

  /** Visible drawer lines and counts. Hidden gifts stay out of both. */
  function getVisibleCartItems(items) {
    return (items || []).filter((item) => !isHiddenInCart(item));
  }

  function getVisibleItemCount(items) {
    return getVisibleCartItems(items).reduce((sum, item) => sum + (item.quantity || 0), 0);
  }

  function getFakeCartPricingState(items, itemCount) {
    const tiers = getDisplayDiscountTiers();
    const paidCount = itemCount == null ? getPaidItemCount(items) : itemCount;
    const activeDiscount = resolveActiveDisplayDiscount(paidCount, tiers);
    return calculateDisplayPricing(items, activeDiscount);
  }

  /**
   * Post-display-discount cart totals, exposed for the progress bar so free
   * shipping / gift thresholds evaluate on the AFTER-discount subtotal — matching
   * Shopify checkout, where a discount that drops the subtotal below the threshold
   * removes free shipping (verified: US $19.20 post-discount is charged shipping).
   */
  window.FakeCartPricing = {
    getDiscountedSubtotal() {
      const items = window.FakeCart.getItems();
      return getFakeCartPricingState(items, getPaidItemCount(items)).subtotalFinal;
    },
    getState() {
      const items = window.FakeCart.getItems();
      return getFakeCartPricingState(items, getPaidItemCount(items));
    },
  };

  function findLinePricing(pricingState, itemKey) {
    return pricingState.lines.find((line) => line.key === itemKey) || null;
  }

  function getMinusButtonAttrs(quantity) {
    return (quantity || 0) <= 1 ? ' disabled aria-disabled="true"' : "";
  }

  function updateMinusButtonState(row, quantity) {
    const minusBtn = row.querySelector('[data-fake-cart-qty="-1"]');
    if (!minusBtn) return;

    const atMin = (quantity || 0) <= 1;
    minusBtn.classList.toggle("disabled", atMin);

    if (atMin) {
      minusBtn.setAttribute("disabled", "");
      minusBtn.setAttribute("aria-disabled", "true");
    } else {
      minusBtn.removeAttribute("disabled");
      minusBtn.removeAttribute("aria-disabled");
    }
  }

  function describeLine(item) {
    const title = item.title || "This item";
    if (item.variant_title && item.variant_title !== "Default Title") {
      return `${title} (${item.variant_title})`;
    }
    return title;
  }

  /**
   * Drop cart lines the catalog says cannot be bought (drafted, deleted, sold out).
   * Removal is silent to the customer — the line just disappears — and the reason
   * is logged by the availability module. Never throws: a failed check leaves the
   * cart exactly as it was.
   */
  async function runAvailabilityCheck(options = {}) {
    if (!window.FakeCartAvailability) return null;

    const result = await window.FakeCartAvailability.validate(options);
    if (result?.checked && result.removed.length > 0) {
      syncFakeCartUI({ forceFullRender: true });
    }

    return result;
  }

  function renderVariantOptions(item) {
    if (!item.variant_title || item.variant_title === "Default Title") return "";

    return `<div class="product-option"><dt></dt><dd>${escapeHtml(item.variant_title)}</dd></div>`;
  }

  function renderPriceBlock(linePricing, freeGift) {
    if (freeGift) {
      const compareCents = linePricing?.displayCompareCents || linePricing?.compareCents || 0;
      if (compareCents > 0) {
        return `
          <div class="gb-price-drawer">
            <s class="cart-item__old-price">${formatMoney(compareCents)}</s>
            <strong class="cart-item__final-price">Free</strong>
          </div>
        `;
      }
      return `<div class="gb-price-drawer"><strong class="cart-item__final-price">Free</strong></div>`;
    }

    const finalCents = linePricing?.finalCents || 0;
    const compareCents = linePricing?.displayCompareCents || 0;
    const savings = compareCents > finalCents ? compareCents - finalCents : 0;

    let priceHtml = `<div class="gb-price-drawer"><strong class="cart-item__final-price">${formatMoney(finalCents)}</strong></div>`;

    if (compareCents > finalCents) {
      priceHtml = `
        <div class="gb-price-drawer">
          <s class="cart-item__old-price">${formatMoney(compareCents)}</s>
          <strong class="cart-item__final-price">${formatMoney(finalCents)}</strong>
        </div>
      `;
    }

    if (savings > 0) {
      priceHtml += `<div class="gb-price-drawer-saving">Save ${formatMoney(savings)}</div>`;
    }

    return priceHtml;
  }

  function renderDiscountTags(discountName) {
    const items = discountName
      ? `<li class="discounts__discount">${DISCOUNT_ICON_SVG}${escapeHtml(discountName)}</li>`
      : "";

    return `
      <ul class="discounts list-unstyled" data-fake-cart-line-discounts role="list" aria-label="Discount">
        ${items}
      </ul>
    `;
  }

  function renderCartItemRow(item, index, linePricing, discountName) {
    const title = escapeHtml(item.title || "Product");
    const url = escapeHtml(item.url || "#");
    const lineKey = escapeHtml(item.key);
    const freeGift = isFreeGiftLine(item);
    const rowClass = freeGift ? "cart-item cart-item--free-gift" : "cart-item";
    const image = item.image
      ? `<img class="cart-item__image" src="${escapeHtml(item.image)}" alt="" loading="lazy" width="120" height="120">`
      : "";
    const mediaHtml = image
      ? freeGift
        ? `<span class="cart-item__link cart-item__link--disabled" aria-hidden="true"></span>${image}`
        : `<a href="${url}" class="cart-item__link" tabindex="-1" aria-hidden="true"> </a>${image}`
      : "";
    const titleHtml = freeGift
      ? `<span class="cart-item__name h4 break">${title}</span>`
      : `<a href="${url}" class="cart-item__name h4 break">${title}</a>`;
    const giftLineDiscounts = freeGift
      ? renderDiscountTags("Free gift")
      : renderDiscountTags(discountName);

    // Free gifts: no qty picker / trash — quantity is locked to metaobject how_many.
    const quantityControlsHtml = freeGift
      ? giftLineDiscounts
      : `
              <div class="quantity-popover-container">
                <div class="quantity cart-quantity">
                  <button class="quantity__button${(item.quantity || 0) <= 1 ? " disabled" : ""}" name="minus" type="button" data-fake-cart-qty="-1" data-line-key="${lineKey}" aria-label="Decrease quantity"${getMinusButtonAttrs(item.quantity)}>
                    <span class="svg-wrapper content">${MINUS_ICON_SVG}</span>
                  </button>
                  <input
                    class="quantity__input"
                    type="number"
                    value="${item.quantity}"
                    min="1"
                    data-quantity-variant-id="${item.id}"
                    data-fake-cart-qty-input
                    data-line-key="${lineKey}"
                    aria-label="Quantity"
                  >
                  <button class="quantity__button" name="plus" type="button" data-fake-cart-qty="1" data-line-key="${lineKey}" aria-label="Increase quantity">
                    <span class="svg-wrapper content">${PLUS_ICON_SVG}</span>
                  </button>
                </div>
                <button
                  type="button"
                  class="cart-remove-button no-enhanced-effects"
                  data-fake-cart-remove
                  data-line-key="${lineKey}"
                  aria-label="Remove ${title}"
                >
                  <span class="svg-wrapper gb-cart-del-img content">${REMOVE_ICON_SVG}</span>
                </button>
              </div>
              ${giftLineDiscounts}
            `;

    return `
      <tr id="FakeCartDrawer-Item-${index}" class="${rowClass}" role="row" data-fake-cart-line="${lineKey}"${freeGift ? ' data-fake-cart-free-gift="true"' : ""}>
        <td class="cart-item__media" role="cell">
          ${mediaHtml}
        </td>
        <td class="cart-item__details" role="cell">
          <div class="cart-details-wrapper">
            <div class="cart-items-wrapper">
              ${titleHtml}
              ${renderVariantOptions(item)}
            </div>
            <div class="cart-item__price-wrapper">
              ${renderPriceBlock(linePricing, freeGift)}
            </div>
          </div>
          <div class="cart-item__quantity" role="cell">
            <div class="cart-item__quantity-wrapper quantity-popover-wrapper">
              ${quantityControlsHtml}
            </div>
          </div>
        </td>
      </tr>
    `;
  }

  function updateCartItemRow(row, item, linePricing, discountName) {
    const freeGift = isFreeGiftLine(item);
    const qtyInput = row.querySelector("[data-fake-cart-qty-input]");
    if (qtyInput && document.activeElement !== qtyInput && qtyInput.value !== String(item.quantity)) {
      qtyInput.value = String(item.quantity);
    }

    if (!freeGift) {
      updateMinusButtonState(row, item.quantity);
    }

    const priceWrapper = row.querySelector(".cart-item__price-wrapper");
    if (priceWrapper) {
      priceWrapper.innerHTML = renderPriceBlock(linePricing, freeGift);
    }

    const discountContainer = row.querySelector("[data-fake-cart-line-discounts]");
    if (discountContainer) {
      const label = freeGift ? "Free gift" : discountName;
      discountContainer.innerHTML = label
        ? `<li class="discounts__discount">${DISCOUNT_ICON_SVG}${escapeHtml(label)}</li>`
        : "";
    }
  }

  function updateFooterTotals(drawer, pricingState, itemCount) {
    const itemCountEl = drawer.querySelector("[data-fake-cart-item-count]");
    const totalEl = drawer.querySelector("[data-fake-cart-total]");
    const totalCompareEl = drawer.querySelector("[data-fake-cart-total-compare]");

    if (itemCountEl) {
      itemCountEl.textContent = getItemsTotalLabel(drawer, itemCount);
    }

    if (totalEl) {
      totalEl.textContent = formatMoney(pricingState.subtotalFinal);
    }

    if (totalCompareEl) {
      if (pricingState.subtotalCompare > pricingState.subtotalFinal) {
        totalCompareEl.textContent = formatMoney(pricingState.subtotalCompare);
        totalCompareEl.hidden = false;
      } else {
        totalCompareEl.textContent = "";
        totalCompareEl.hidden = true;
      }
    }
  }

  function renderFakeCartItems({ forceFullRender = false } = {}) {
    const drawer = document.querySelector("fake-cart-drawer");
    const container = drawer?.querySelector("[data-fake-cart-items]");
    if (!container || !window.FakeCart) return;

    const allItems = window.FakeCart.getItems();
    const items = getVisibleCartItems(allItems);
    const itemCount = getVisibleItemCount(allItems);
    const pricingState = getFakeCartPricingState(allItems, getPaidItemCount(allItems));
    const discountName = pricingState.discountName || "";
    const nextKeys = items.map((item) => item.key);
    const keysChanged =
      forceFullRender ||
      nextKeys.length !== lastItemKeys.length ||
      nextKeys.some((key, index) => key !== lastItemKeys[index]);

    if (keysChanged) {
      container.innerHTML = items
        .map((item, index) => {
          const linePricing = findLinePricing(pricingState, item.key);
          return renderCartItemRow(item, index + 1, linePricing, discountName);
        })
        .join("");
      lastItemKeys = nextKeys.slice();
    } else {
      items.forEach((item) => {
        const row = container.querySelector(`[data-fake-cart-line="${escapeSelector(item.key)}"]`);
        if (!row) return;
        updateCartItemRow(row, item, findLinePricing(pricingState, item.key), discountName);
      });
    }

    updateFooterTotals(drawer, pricingState, itemCount);
  }

  function updateCartCountBubbles(itemCount) {
    const drawer = document.querySelector("fake-cart-drawer");

    drawer?.querySelectorAll(".gb-cart-total-item, [data-fake-cart-count]").forEach((el) => {
      el.textContent = String(itemCount);
      el.hidden = itemCount <= 0;
    });

    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    let bubble = cartLink.querySelector(".cart-count-bubble");

    if (itemCount <= 0) {
      if (bubble) bubble.style.display = "none";
      return;
    }

    if (!bubble) {
      bubble = document.createElement("div");
      bubble.className = "cart-count-bubble";
      bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
      cartLink.appendChild(bubble);
    }

    bubble.style.display = "";

    const bubbleVisible = bubble.querySelector("[aria-hidden='true']");
    const bubbleHidden = bubble.querySelector(".visually-hidden");

    if (bubbleVisible) bubbleVisible.textContent = String(itemCount);
    if (bubbleHidden) {
      bubbleHidden.textContent = `${itemCount} item${itemCount === 1 ? "" : "s"}`;
    }
  }

  function syncFakeCartUI(options = {}) {
    const drawer = document.querySelector("fake-cart-drawer");
    if (!drawer || !window.FakeCart) return;

    const { isEmpty } = window.FakeCart.getState();
    const itemCount = getVisibleItemCount(window.FakeCart.getItems());
    drawer.classList.toggle("is-empty", isEmpty);

    const emptyView = drawer.querySelector("[data-fake-cart-empty]");
    const filledView = drawer.querySelector("[data-fake-cart-filled]");
    if (emptyView) emptyView.hidden = !isEmpty;
    if (filledView) filledView.hidden = isEmpty;

    updateCartCountBubbles(itemCount);

    const checkoutButton = drawer.querySelector("[data-fake-cart-checkout]");
    if (checkoutButton) checkoutButton.disabled = window.FakeCart.isEmpty();

    document.querySelectorAll(".ey-qty-cart").forEach((wrapper) => {
      const variantId = wrapper.dataset.productId;
      const input = wrapper.querySelector(".ey-qty-input");
      if (!variantId || !input) return;

      input.value = String(sumVariantQuantity(variantId));
    });

    if (isEmpty) {
      const container = drawer.querySelector("[data-fake-cart-items]");
      if (container) container.innerHTML = "";
      lastItemKeys = [];
    } else {
      renderFakeCartItems(options);
    }
  }

  function getStoreRoot() {
    return window.Shopify?.routes?.root || "/";
  }

  function getFakeCartDrawer() {
    return document.querySelector("fake-cart-drawer");
  }

  /**
   * Source of truth for market country: Liquid localization on the drawer,
   * then Shopify.country, then GeoIP suggestion.
   * NOTE: Storefront market (CA) != checkout shipping address country (often
   * defaults to shop primary US). We use the Liquid country for both session
   * commit and checkout address prefill.
   */
  function getLiquidLocalization() {
    const drawer = getFakeCartDrawer();
    return {
      countryCode: drawer?.dataset?.localizationCountry || window.Shopify?.country || null,
      countryName: drawer?.dataset?.localizationCountryName || null,
      locale: drawer?.dataset?.localizationLocale || window.Shopify?.locale || "en",
    };
  }

  function getCheckoutUrl(countryName) {
    const root = getStoreRoot();
    const url = new URL(`${root}checkout`, window.location.origin);

    // Prefill checkout shipping Country/region. Storefront Markets alone does
    // not set this field — guest checkout often still defaults to the shop's US.
    if (countryName) {
      url.searchParams.set("checkout[shipping_address][country]", countryName);
    }

    return `${url.pathname}${url.search}`;
  }

  /**
   * Ask Shopify's GeoIP which country this visitor should use.
   * Returns ISO country code (e.g. "CA") or null if unavailable.
   */
  async function detectSuggestedCountryCode(excludeCountry) {
    const root = getStoreRoot();
    const current = excludeCountry || "";
    const url =
      `${root}browsing_context_suggestions.json` +
      `?country[enabled]=true` +
      (current ? `&country[exclude]=${encodeURIComponent(current)}` : "");

    try {
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return null;

      const data = await response.json();
      const detected =
        data?.detected_values?.country?.handle ||
        data?.suggestions?.[0]?.parts?.country?.handle ||
        null;

      console.log("[fake-cart] browsing_context_suggestions", {
        shopifyCountry: window.Shopify?.country,
        detectedValues: data?.detected_values?.country,
        suggestion: data?.suggestions?.[0]?.parts?.country,
        resolved: detected,
      });

      return detected;
    } catch (error) {
      console.warn("Fake cart: browsing_context_suggestions failed", error);
      return null;
    }
  }

  /**
   * Commit country to the Shopify storefront session (Markets / cart context).
   * Uses POST /localization — cart/update.js does not set market country.
   */
  async function commitLocalizationCountry(countryCode, localeCode, { force = false } = {}) {
    if (!countryCode) return false;
    if (!force && window.Shopify?.country === countryCode) {
      console.log("[fake-cart] localization already active", { countryCode });
      return false;
    }

    const root = getStoreRoot();
    const formData = new FormData();
    formData.append("_method", "PUT");
    formData.append("country_code", countryCode);
    formData.append("locale_code", localeCode || "en");

    try {
      const response = await fetch(`${root}localization`, {
        method: "POST",
        body: formData,
        credentials: "same-origin",
        redirect: "manual",
      });

      if (window.Shopify) {
        window.Shopify.country = countryCode;
      }

      console.log("[fake-cart] localization committed", {
        countryCode,
        status: response.status,
        type: response.type,
      });
      return true;
    } catch (error) {
      console.warn("Fake cart: localization commit failed", error);
      return false;
    }
  }

  /**
   * Resolve checkout destination country and commit Markets session.
   * Prefer Liquid localization (already CA in your guest test) over Shopify.country,
   * which can lag; fall back to GeoIP only when Liquid has no country.
   */
  async function ensureCheckoutLocalization() {
    const liquid = getLiquidLocalization();
    const geo = await detectSuggestedCountryCode(liquid.countryCode || window.Shopify?.country);
    const countryCode = liquid.countryCode || geo || window.Shopify?.country || null;
    const countryName = liquid.countryName || null;
    const locale = liquid.locale || "en";

    console.log("[fake-cart] ensureCheckoutLocalization", {
      liquid,
      geo,
      shopifyCountry: window.Shopify?.country,
      resolvedCountryCode: countryCode,
      resolvedCountryName: countryName,
    });

    if (countryCode) {
      // Force commit so cart clear/add run under an explicit market cookie write.
      await commitLocalizationCountry(countryCode, locale, { force: true });
    }

    return { countryCode, countryName, locale };
  }

  function buildCartAddItems(items) {
    return items.map((item) => {
      const properties = { ...(item.properties || {}) };
      delete properties._selling_plan_id;

      const line = {
        id: item.id,
        quantity: item.quantity,
        properties: {
          ...properties,
          _slpt: properties._slpt ?? "",
        },
      };

      if (item.selling_plan_id) {
        line.selling_plan = item.selling_plan_id;
      }

      return line;
    });
  }

  async function fetchCartJson(url, options = {}) {
    const response = await fetch(url, {
      credentials: "same-origin",
      // /cart.js must never come from the browser cache — back-forward navigation
      // will happily reuse a response from before the checkout clear+add, and
      // reconcile would then rebuild the fake cart from a stale snapshot.
      cache: "no-store",
      ...options,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(options.headers || {}),
      },
    });

    const text = await response.text();
    let data = null;

    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }

    if (!response.ok) {
      const message = data?.description || data?.message || `Request failed (${response.status})`;
      const error = new Error(message);
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  function resetCheckoutUiState() {
    checkoutInProgress = false;

    const drawer = document.querySelector("fake-cart-drawer");
    const button = drawer?.querySelector("[data-fake-cart-checkout]");
    if (!button || !window.FakeCart) return;

    button.disabled = window.FakeCart.isEmpty();
    button.removeAttribute("aria-busy");
    button.classList.remove("loading");
  }

  function setCheckoutPending() {
    try {
      sessionStorage.setItem(CHECKOUT_PENDING_KEY, "1");
    } catch {
      // sessionStorage unavailable (private mode quotas, etc.)
    }
  }

  function clearCheckoutPending() {
    try {
      sessionStorage.removeItem(CHECKOUT_PENDING_KEY);
    } catch {
      // ignore
    }
  }

  function isCheckoutPending() {
    try {
      return sessionStorage.getItem(CHECKOUT_PENDING_KEY) === "1";
    } catch {
      return false;
    }
  }

  /**
   * Map Ajax Cart line → fake cart line.
   * Identity/qty come from Shopify; unit prices do NOT use Shopify-applied discounts
   * (final_price / discounted_price / line discounts). Display pricing stays on
   * fake-cart display_discounts metaobject tiers.
   *
   * Price preference:
   * 1) Existing fake-cart line (PLP/PDP catalog prices + compare-at)
   * 2) Cart original_price (variant price before automatic discounts)
   */
  function mapShopifyCartItem(item, previousByVariantId) {
    if (!item) return null;

    const variantId = item.variant_id ?? item.id;
    if (!variantId) return null;

    const previous = previousByVariantId?.get(String(variantId)) || null;

    // Never use final_price / discounted_price — those include real cart discounts.
    const catalogPrice =
      (previous && previous.price > 0 ? previous.price : 0) ||
      parseInt(item.original_price, 10) ||
      parseInt(item.price, 10) ||
      0;

    const compareAt =
      (previous && previous.compare_at_price > catalogPrice ? previous.compare_at_price : 0) ||
      (parseInt(item.compare_at_price, 10) > catalogPrice ? parseInt(item.compare_at_price, 10) : 0) ||
      0;

    return {
      id: variantId,
      product_id: item.product_id ?? previous?.product_id ?? null,
      quantity: item.quantity || 1,
      properties: item.properties || {},
      selling_plan_id: item.selling_plan_allocation?.selling_plan?.id || previous?.selling_plan_id || null,
      title: item.product_title || item.title || previous?.title || "",
      variant_title: item.variant_title || previous?.variant_title || "",
      price: catalogPrice,
      compare_at_price: compareAt,
      image: item.image || item.featured_image?.url || previous?.image || "",
      url: item.url || previous?.url || "",
    };
  }

  /**
   * Hybrid reconcile after a checkout attempt (sessionStorage pending flag):
   * - Shopify cart has items → abandon / edited checkout → rebuild fake cart from /cart.js
   * - Shopify cart empty → assume purchase (or cart wiped) → clear fake cart
   * - No pending flag → normal browsing → leave fake cart alone
   *
   * Does not clear the Shopify cart here: clearing would break an in-progress checkout
   * in another tab. The next checkoutFakeCart() already clear+adds before redirect.
   */
  async function reconcileWithShopifyCart() {
    if (!window.FakeCart || reconcileInProgress) return;
    if (!document.querySelector("fake-cart-drawer")) return;
    if (!isCheckoutPending()) return;

    reconcileInProgress = true;

    try {
      const cart = await fetchCartJson("/cart.js", { method: "GET" });
      const shopifyItems = Array.isArray(cart?.items) ? cart.items : [];

      if (shopifyItems.length > 0) {
        // Keep catalog prices from fake cart; only adopt identity/qty from Shopify.
        const previousByVariantId = new Map();
        window.FakeCart.getItems().forEach((line) => {
          const key = String(line.id);
          if (!previousByVariantId.has(key)) previousByVariantId.set(key, line);
        });

        const mapped = shopifyItems
          .map((item) => mapShopifyCartItem(item, previousByVariantId))
          .filter(Boolean);
        window.FakeCart.replaceItems(mapped);
        clearCheckoutPending();

        console.log("[fake-cart] reconciled from Shopify cart", {
          itemCount: cart.item_count,
          lineCount: mapped.length,
        });
        return;
      }

      window.FakeCart.clear();
      clearCheckoutPending();
      console.log("[fake-cart] cleared after checkout return (empty Shopify cart + pending)");
    } catch (error) {
      console.warn("Fake cart: reconcile with Shopify cart failed", error);
    } finally {
      reconcileInProgress = false;
      resetCheckoutUiState();
    }
  }

  /**
   * Push the fake cart into the real Shopify cart, surviving lines Shopify won't
   * accept.
   *
   * /cart/add.js is atomic: one unavailable variant rejects the whole batch, which
   * is how a drafted product used to lock checkout permanently. On a 422 we retry
   * line by line — sequentially, since concurrent writes to one cart race — to
   * isolate exactly which lines Shopify refuses.
   *
   * Only a 422 counts as a rejection. A 429/5xx/network failure is transient and
   * must not cost the customer their line, so it propagates instead.
   *
   * @returns {Promise<{added: object[], rejected: {item: object, message: string}[]}>}
   */
  async function addItemsToShopifyCart(items) {
    const addUrl = window.routes?.cart_add_url || "/cart/add.js";

    try {
      await fetchCartJson(addUrl, {
        method: "POST",
        body: JSON.stringify({ items: buildCartAddItems(items) }),
      });
      return { added: items, rejected: [] };
    } catch (error) {
      if (error?.status !== 422) throw error;

      // The batch was atomic, so nothing landed — the cart is still empty here.
      const added = [];
      const rejected = [];

      for (const item of items) {
        try {
          await fetchCartJson(addUrl, {
            method: "POST",
            body: JSON.stringify({ items: buildCartAddItems([item]) }),
          });
          added.push(item);
        } catch (lineError) {
          if (lineError?.status !== 422) throw lineError;
          rejected.push({ item, message: lineError.message });
        }
      }

      return { added, rejected };
    }
  }

  async function checkoutFakeCart(button) {
    if (checkoutInProgress || !window.FakeCart) return;
    if (window.FakeCart.isEmpty()) return;

    checkoutInProgress = true;
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    button.classList.add("loading");

    try {
      // Catch a drafted / deleted / sold-out product before touching the real
      // cart, so the common case never reaches the 422 recovery below.
      await runAvailabilityCheck({ force: true });

      // Everything in the cart went away — drop back to the empty drawer.
      if (window.FakeCart.isEmpty()) {
        console.warn("[fake-cart] checkout aborted: no purchasable items remain");
        resetCheckoutUiState();
        syncFakeCartUI({ forceFullRender: true });
        return;
      }

      const items = window.FakeCart.getItems();

      // Markets (shared domain): commit Liquid/GeoIP country, then cart sync.
      // Also prefill checkout shipping country — market context alone often
      // leaves guest Country/region stuck on the shop default (US).
      const localization = await ensureCheckoutLocalization();

      await fetchCartJson("/cart/clear.js", { method: "POST", body: "{}" });

      const { added, rejected } = await addItemsToShopifyCart(items);

      // Anything Shopify refused went out of the catalog between the check above
      // and this add (or was never checkable for want of a handle). Drop it and
      // stop — never redirect to checkout with an order the customer has not seen.
      // They click checkout again on a cart that now matches what they will pay for.
      if (rejected.length > 0) {
        window.FakeCart.removeLines(rejected.map((entry) => entry.item.key));
        console.warn(
          "[fake-cart] removed lines rejected by Shopify:",
          rejected.map((entry) => `${describeLine(entry.item)} — ${entry.message}`).join("; "),
        );

        resetCheckoutUiState();
        syncFakeCartUI({ forceFullRender: true });
        return;
      }

      // Mark checkout attempt so return with an empty Shopify cart can clear localStorage.
      // If the customer abandons, reconcile rebuilds from /cart.js instead.
      setCheckoutPending();

      const checkoutUrl = getCheckoutUrl(localization?.countryName);
      console.log("[fake-cart] redirecting to checkout", {
        checkoutUrl,
        countryCode: localization?.countryCode,
        countryName: localization?.countryName,
      });
      window.location.assign(checkoutUrl);
    } catch (error) {
      // Transient failure (network, 429, 5xx). The cart is intact — the button
      // resets and the customer can retry. Console only, no customer-facing message.
      console.error("[fake-cart] checkout failed", error);
      clearCheckoutPending();
      resetCheckoutUiState();
    }
  }

  function handleCartLineQuantityInput(event) {
    if (!window.FakeCart) return;
    if (!event.target.closest("fake-cart-drawer")) return;

    const input = event.target.closest("[data-fake-cart-qty-input]");
    if (!input) return;

    if (event.type === "keydown") {
      if (event.key === "Enter") {
        event.preventDefault();
        input.blur();
      }
      return;
    }

    if (event.type === "change") {
      commitLineQuantityInput(input);
    }
  }

  function handleCartLineAction(event) {
    if (!window.FakeCart) return;
    if (!event.target.closest("fake-cart-drawer")) return;

    const checkoutButton = event.target.closest("[data-fake-cart-checkout]");
    if (checkoutButton) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      checkoutFakeCart(checkoutButton);
      return;
    }

    const qtyButton = event.target.closest("[data-fake-cart-qty]");
    if (qtyButton) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (qtyButton.disabled || qtyButton.getAttribute("aria-disabled") === "true") return;

      const key = getLineKey(qtyButton);
      const qtyItem = window.FakeCart.getItems().find((line) => line.key === key);
      if (qtyItem && isFreeGiftLine(qtyItem)) return;

      const delta = parseInt(qtyButton.getAttribute("data-fake-cart-qty"), 10);
      if (!key || !Number.isFinite(delta) || delta === 0) return;
      if (qtyButton.getAttribute("aria-disabled") === "true" || qtyButton.disabled) return;

      if (delta < 0) {
        const item = window.FakeCart.getItems().find((line) => line.key === key);
        if (!item || item.quantity <= 1) return;
      }

      window.FakeCart.changeLineQuantity(key, delta);
      return;
    }

    const removeButton = event.target.closest("[data-fake-cart-remove]");
    if (removeButton) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (removeButton.disabled || removeButton.getAttribute("aria-disabled") === "true") return;

      const key = getLineKey(removeButton);
      if (!key) return;

      const removeItem = window.FakeCart.getItems().find((line) => line.key === key);
      if (removeItem && isFreeGiftLine(removeItem)) return;

      window.FakeCart.removeLine(key);
    }
  }

  function bindFakeCartActions() {
    if (bindFakeCartActions.bound) return;
    bindFakeCartActions.bound = true;
    document.addEventListener("click", handleCartLineAction, true);
    document.addEventListener("change", handleCartLineQuantityInput, true);
    document.addEventListener("keydown", handleCartLineQuantityInput, true);
  }

  class FakeCartDrawer extends HTMLElement {
    constructor() {
      super();

      this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
      this.addEventListener("click", (event) => {
        if (event.target?.id === "FakeCartDrawer-Overlay") {
          this.close();
        }
      });

      this.setHeaderCartIconAccessibility();
    }

    connectedCallback() {
      bindFakeCartActions();
      syncFakeCartUI({ forceFullRender: true });
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector("#cart-icon-bubble");
      if (!cartLink) return;

      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");

      cartLink.addEventListener("click", (event) => {
        event.preventDefault();
        this.open(cartLink);
      });

      cartLink.addEventListener("keydown", (event) => {
        if (event.code.toUpperCase() === "SPACE") {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    open(triggeredBy) {
      if (triggeredBy) this.activeElement = triggeredBy;

      resetCheckoutUiState();
      syncFakeCartUI();
      runAvailabilityCheck().catch(() => {});

      setTimeout(() => {
        this.classList.add("animate", "active");
      });

      this.addEventListener(
        "transitionend",
        () => {
          const container = document.getElementById("FakeCartDrawer");
          const focusTarget = this.classList.contains("is-empty")
            ? this.querySelector(".drawer__inner-empty") || this.querySelector(".cart__empty-text")
            : this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
          if (typeof trapFocus === "function" && container) {
            trapFocus(container, focusTarget);
          }
        },
        { once: true },
      );

      document.body.classList.add("overflow-hidden");
    }

    close() {
      if (this.classList.contains("customization-mode")) return;

      this.classList.remove("active");
      if (typeof removeTrapFocus === "function") {
        removeTrapFocus(this.activeElement);
      }
      document.body.classList.remove("overflow-hidden");
    }
  }

  customElements.define("fake-cart-drawer", FakeCartDrawer);

  bindFakeCartActions();

  async function bootstrapFakeCartUi() {
    await reconcileWithShopifyCart();
    syncFakeCartUI({ forceFullRender: true });

    // Non-blocking: the drawer is already correct against localStorage, and this
    // only ever prunes it. Runs on every load — it is the only moment a cart sitting
    // in localStorage learns that a product went to draft.
    runAvailabilityCheck().catch(() => {});
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      bootstrapFakeCartUi();
    });
  } else {
    bootstrapFakeCartUi();
  }

  document.addEventListener("fake-cart:updated", () => syncFakeCartUI());

  // Back from checkout restores this page from bfcache — the JS heap comes back
  // exactly as it was frozen, so any in-flight request from before the navigation
  // is now a promise that may never settle. Clear the guards those requests own
  // before re-running, otherwise reconcile bails on a stuck reconcileInProgress
  // (leaving both the drawer and the pending flag stale) and the availability
  // check hangs on a dead inFlight promise.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      reconcileInProgress = false;
      window.FakeCartAvailability?.reset();
      resetCheckoutUiState();
      bootstrapFakeCartUi();
    }
  });
})();
